import React, { useEffect, useContext, useState } from "react";
import { ContainerContext } from "context/Container";
import {
    ContentObjectSchema,
    transform as contentTransformer,
} from "schemas/Content";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import ContentPicker from "components/DesignSystem/ContentPicker";
import SecondaryButton from "components/Buttons/SecondaryButton";
import { apiGet } from "helpers/Request";
import { useAuth } from "hooks/Auth";
import { useFormikContext } from "formik";
import { uniq } from "lodash";
import Label from "components/FormElements/Label";
import { SwitchVerticalIcon, TrashIcon } from "@heroicons/react/outline";
import flatCube from "assets/flat-cube.svg";
import { Link } from "react-router-dom";
import Pill from "components/Text/Pill";
import { packClasses } from "Utils";
import { background as statusColors } from "constants/status-colors";

export default function ContentList(props) {
    const { state } = useContext(ContainerContext);
    const [listItems, setListItems] = useState([]);
    const [contentPickerState, setContentPickerState] = useState(false);
    const { setFieldValue } = useFormikContext();

    const openPicker = () => {
        setContentPickerState(true);
    };
    const closePicker = () => {
        setContentPickerState(false);
    };
    const handleMediaSelect = (items) => {
        // onChange(item);
        const newItems = uniq(items.map((i) => i.uuid).concat(listItems));
        setListItems(newItems);
        setFieldValue("sort_order", newItems);
        setFieldValue(
            "content",
            newItems.map((uuid) => ({ uuid }))
        );
    };

    const requestRemove = (item) => {
        const newItems = listItems.filter((i) => i !== item);
        setListItems(newItems);
        setFieldValue("sort_order", newItems);
        setFieldValue(
            "content",
            newItems.map((uuid) => ({ uuid }))
        );
    };

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };

    const onDragEnd = (result) => {
        if (!result.destination) {
            return;
        }

        const items = reorder(
            listItems,
            result.source.index,
            result.destination.index
        );

        setListItems(items);
        setFieldValue("sort_order", items);
        setFieldValue(
            "content",
            items.map((uuid) => ({ uuid }))
        );
    };

    useEffect(() => {
        if (state.container.sort_order) {
            setListItems(state.container.sort_order);
        }
    }, [state.container, setListItems]);

    return (
        <div>
            <Label>Attached Content</Label>
            <div className="space-y-4">
                {listItems.length ? (
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="sort_order">
                            {(provided, snapshot) => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                >
                                    {listItems.map((item, index) => (
                                        <Draggable
                                            key={item}
                                            draggableId={item}
                                            index={index}
                                        >
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                >
                                                    <div className="flex-cols my-2 flex items-center space-x-2 rounded bg-white p-2 shadow">
                                                        <div
                                                            {...provided.dragHandleProps}
                                                            aria-label="Drag to reorder"
                                                        >
                                                            <SwitchVerticalIcon className="h-6 w-6 text-gray-600" />
                                                        </div>
                                                        <DraggableContentItem
                                                            uuid={item}
                                                        />
                                                        <div>
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    requestRemove(
                                                                        item
                                                                    )
                                                                }
                                                            >
                                                                <TrashIcon className="h-6 w-6 text-gray-600" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                ) : null}
            </div>

            <SecondaryButton onClick={openPicker} className="mt-2">
                Add content
            </SecondaryButton>

            <ContentPicker
                open={contentPickerState}
                onSelect={handleMediaSelect}
                onRequestClose={closePicker}
                allowMultiple={true}
                mediaType={state.container.type === "gallery" ? "image" : null}
                type={state.container.type === "gallery" ? "media" : "content"}
                listType={state.container.type === "gallery" ? "grid" : "list"}
            />
        </div>
    );
}

export function DraggableContentItem(props) {
    const auth = useAuth();
    const { state } = useContext(ContainerContext);
    const [item, setItem] = useState(ContentObjectSchema);
    const [fetchingItem, setFetchingItem] = useState(true);
    const { uuid } = props;

    useEffect(() => {
        apiGet(`/v4/content/${uuid}`, auth.publication, {
            include: "attachment",
        })
            .then((res) => {
                setFetchingItem(false);
                setItem(contentTransformer(res));
            })
            .catch((e) => {
                console.log(e);
            });
    }, [uuid, auth.publication, state.container.content]);

    if (fetchingItem) {
        return (
            <div className="h-20 w-full animate-pulse bg-gray-300 text-sm text-gray-300">
                Loading...
            </div>
        );
    }

    return (
        <div className="h-20 w-full text-sm text-gray-800">
            {item.uuid ? (
                <div className="flex-cols flex items-start space-x-2">
                    <div>
                        {item.attachment ? (
                            <img
                                src={
                                    item.attachment.imgix_url +
                                    "?w=100&ar=1:1&dpr=2&auto=format&fit=crop&crop=faces&q=80"
                                }
                                alt={item.slug}
                                className="pointer-events-none h-20 w-20"
                            />
                        ) : (
                            <img
                                src={flatCube}
                                alt={item.slug}
                                className="pointer-events-none h-20 w-20"
                            />
                        )}
                    </div>
                    <div className="space-y-2 overflow-hidden">
                        <Link
                            to={item.edit_url}
                            className="color-gray-400 block truncate text-sm hover:underline"
                        >
                            {item.title}
                        </Link>
                        <div className="flex space-x-2 align-middle">
                            <div>
                                <span
                                    className={packClasses(
                                        "mx-0 inline-block h-3 w-3 rounded-full leading-none",
                                        statusColors[item.status]
                                    )}
                                >
                                    &nbsp;
                                </span>
                            </div>
                            <Pill
                                className={packClasses(
                                    "border border-transparent bg-gray-100 text-gray-700"
                                )}
                            >
                                {item.type}
                            </Pill>
                            <Pill
                                className={packClasses(
                                    "border border-transparent bg-gray-100 text-gray-700"
                                )}
                            >
                                {item.workflow && item.workflow.uuid ? (
                                    item.workflow.name
                                ) : (
                                    <span className="text-gray-300">None</span>
                                )}
                            </Pill>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex flex-grow items-center justify-center">
                    <span className="inline-block">Unable to load preview</span>
                </div>
            )}
        </div>
    );
}
